import { useNode } from "@craftjs/core";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import useApplyGlobalSettings, { CODEBLOCK_TYPES } from "../../../../hooks/useApplyGlobalSettings";
import { usePaletteColor } from "../../../../hooks/usePaletteColor";
import { updateImageStyles } from "../../../../redux/reducers/PrevStylesCopy/CodeblockDefaultStyles";
import {
  getSelectedSegment,
} from "../../../../redux/selectors/segmentBuilderSelectors";
import { ASSETS } from "../../../../utils/config";
import { BlockIndicator } from "../../Shared/BlockIndicator";
import ImageBlock from "../../Shared/ImageBlock";
import ImageSettings from "./ImageSettings";

export const Image = ({
  src,
  alt,
  width,
  isApplyMarginOnAllSides,
  defaultImage,
  allSideMargin,
  margin,
  borderRadius,
  borderColor,
  borderWidth,
  showImageTitle,
  title,
  hyperlink,
  showHyperlink,
  dropShadowColor,
  alignment,
  image,
}) => {
  const {
    connectors: { connect, drag },
    actions: { setProp },
  } = useNode();

  const selectedSegment = useSelector(getSelectedSegment);
  const selectedSegmentColor = selectedSegment?.settings?.color;
  const { getPaletteColor } = usePaletteColor();
  const { image: imageProps } = useSelector((state) => state.codeblockStyles);
  const dispatch = useDispatch();

  // Initialize with default props from reducer for new components
  useEffect(() => {
    // Set the props from the reducer if they're undefined
    const timeoutId = setTimeout(() => {
      setProp((props) => {
        // Margin properties
        if (props.margin === undefined) {
          props.margin = { ...imageProps.margin };
        }
        
        if (props.isApplyMarginOnAllSides === undefined) {
          props.isApplyMarginOnAllSides = imageProps.isApplyMarginOnAllSides;
        }
        
        if (props.allSideMargin === undefined) {
          props.allSideMargin = imageProps.allSideMargin;
        }
        
        // Width property
        if (props.width === undefined) {
          props.width = imageProps.width;
        }
        
        // Border properties
        if (props.borderRadius === undefined) {
          props.borderRadius = imageProps.borderRadius;
        }
        
        if (props.borderWidth === undefined) {
          props.borderWidth = imageProps.borderWidth;
        }
        
        if (props.borderColor === undefined) {
          props.borderColor = imageProps.borderColor || "suggested_color_0";
        }
        
        // Other style properties
        if (props.dropShadowColor === undefined) {
          props.dropShadowColor = imageProps.dropShadowColor;
        }
        
        if (props.alignment === undefined) {
          props.alignment = imageProps.alignment;
        }
        
        // Image-specific properties
        if (props.showImageTitle === undefined) {
          props.showImageTitle = imageProps.showImageTitle;
        }
        
        if (props.hyperlink === undefined) {
          props.hyperlink = imageProps.hyperlink;
        }
        
        if (props.showHyperlink === undefined) {
          props.showHyperlink = imageProps.showHyperlink;
        }
      });
    }, 5);
    
    // Cleanup timeout if the component unmounts or dependencies change
    return () => clearTimeout(timeoutId);
  }, []);

  // Use the hook to apply global settings when they change
  useApplyGlobalSettings(setProp, CODEBLOCK_TYPES.Image);

  const marginStyle = isApplyMarginOnAllSides
    ? `${allSideMargin}px`
    : margin
    ? `${margin?.top}px ${margin?.right}px ${margin?.bottom}px ${margin?.left}px`
    : undefined;

  const border = borderColor
    ? `${borderWidth}px solid ${getPaletteColor(borderColor)}`
    : "none";

  return (
    <div ref={(ref) => connect(drag(ref))}>
      <BlockIndicator>
        <div
          style={{
            position: "relative",
            width: "100%",
            display: "flex",
            flexDirection: "row",
            justifyContent: alignment,
            padding: marginStyle,
          }}
        >
          <ImageBlock
            src={image.src || src || defaultImage}
            title={image.title || title}
            showImageTitle={showImageTitle}
            showHyperlink={showHyperlink}
            hyperlink={hyperlink}
            style={{
              boxShadow:
                dropShadowColor &&
                `0px 0px 9.3px 0px ${getPaletteColor(dropShadowColor)}`,
              width: width,
              borderRadius,
              border,
            }}
          />
        </div>
      </BlockIndicator>
    </div>
  );
};

export const ImageDefaultProps = {
  image: {
    src: "",
    title: "",
  },
  defaultImage: ASSETS ? ASSETS.segmentEditor.UploadImage : "",
  alt: 'New image',
  height: 260,
  margin: undefined,
  isApplyMarginOnAllSides: undefined,
  allSideMargin: undefined,
  width: undefined,
  borderRadius: undefined,
  borderWidth: undefined,
  showImageTitle: undefined,
  hyperlink: undefined,
  borderColor: undefined,
  showHyperlink: undefined,
  dropShadowColor: undefined,
  alignment: undefined,
  src: "",
  title: "",
};

// Use simple props instead of attempting to read from store at initialization time
Image.craft = {
  props: ImageDefaultProps,
  related: {
    settings: ImageSettings,
  },
  displayName: 'ImageWidget',
};
